import { defineStore } from 'pinia'
import type { Notification } from '~/types/notifications'
import { NotificationEventState, NotificationType } from '~/types/notifications'
import type {RouteRecordName} from "#vue-router";
import {checkRouteName} from "~/utils";

// Timing constants
const INTERVAL = 4000
const WISHLIST_INTERVAL = 2000

// Route blacklist for notifications
const BLACKLISTED_ROUTE_NAMES = [
  'cart',
  'promotion',
  'checkout',
  'confirmation',
  'auth-sign-in'
] as const

/**
 * Check if the current route is blacklisted for notifications
 * @param routeName - The route name to check
 * @returns true if the route is blacklisted, false otherwise
 */
const isRouteBlacklistedForNotifications = (routeName: RouteRecordName): boolean => {
  return !!(routeName && checkRouteName(routeName, BLACKLISTED_ROUTE_NAMES))
}

export const useNotificationsStore = defineStore('notifications', () => {
  const notification = ref<Notification | null>()
  const timer = ref()
  const queue = ref<Notification[]>([])

  const hoverSources = ref<Record<string, boolean>>({
    cart: false,
    drawer: false
  })
  const isHovered = computed(() => Object.values(hoverSources.value).some(Boolean))
  const interval = computed(() => notification.value?.type === NotificationType.WishlistItemAdded ? WISHLIST_INTERVAL : INTERVAL)

  const showNotification = (payload: { notification: Notification, force?: boolean }) => {
    // Check if current route is blacklisted for notifications
    const route = useRoute()
    const currentRouteName = route.name
    const badRoute = isRouteBlacklistedForNotifications(currentRouteName)
    if (badRoute && !payload.force) {
      return // Don't show notification on blacklisted routes unless forced
    }
    if (payload.force || !notification.value) {
      pause()
      notification.value = payload.notification
      notification.value.state = NotificationEventState.NotificationOpen
      if (!isHovered.value) {
        resume()
      }
    } else {
      queue.value.push(payload.notification)
      notification.value.state = NotificationEventState.NotificationOpen
    }
  }

  // Pause the auto-close timer
  const pause = () => {
    if (timer.value) {
      clearTimeout(timer.value)
    }
  }

  // Resume the auto-close timer
  const resume = () => {
    if (timer.value) {
      clearTimeout(timer.value)
    }
    if (isHovered.value) {
      return
    }
    timer.value = setTimeout(() => {
      nextTick()
    }, interval.value) as unknown as number
  }

  // Process next notification in queue
  const nextTick = () => {
    // Check if hovered before closing
    if (isHovered.value) {
      return // Don't close if hovered
    }

    if (queue.value.length) {
      notification.value = queue.value.shift() ?? null
      if (notification.value) {
        notification.value.state = NotificationEventState.NotificationOpen
      }
      resume()
    } else {
      notification.value = null
    }
  }

  // Replace current notification
  const replace = (replacementNotification: Notification | null) => {
    if (replacementNotification?.state === NotificationEventState.NotificationClose) {
      notification.value = null
    } else {
      notification.value = replacementNotification
      if (notification.value) {
        notification.value.state = NotificationEventState.NotificationOpen
        if (!isHovered.value) {
          resume() // <-- Start the timer!
        }
      }
    }
  }

  // Handle hover state changes
  const setHovered = (hover: boolean, source: 'cart' | 'drawer' = 'cart') => {
    hoverSources.value[source] = hover
    if (hover || isHovered.value) {
      return
    }

    // Close notifications immediately if set hover to false
    replace({ state: NotificationEventState.NotificationClose } as Notification)
    nextTick()
  }

  return {
    notification,
    isHovered,
    showNotification,
    replace,
    resume,
    pause,
    setHovered,
    isRouteBlacklistedForNotifications,
    queue,
    timer,
    nextTick
  }
})
